import { api, API_URL } from "../config/apiConfig";
import { handleUnauthorizedOrForbidden } from "../config/varConfig";

// Fetch all astrologers with pagination, search, and filters
export const getAstrologers = async (params = {}) => {
  try {
    const response = await api.get(`${API_URL}/astrologers`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search || "",
        status: params.status || "",
        busy: params.busy ?? "", // handles false correctly
        startDate: params.startDate || "",
        endDate: params.endDate || "",
        sortOrder: params.sortOrder || "desc",
      },
    });

    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// Fetch astrologer wallet history
export const getAstrologerWalletHistory = async ({
  id,
  page = 1,
  limit = 10,
  startDate = "",
  endDate = "",
}) => {
  try {
    const response = await api.get(
      `${API_URL}/astrologers/getWalletHistory/${id}?page=${page}&limit=${limit}&startDate=${startDate ? startDate : ""}&endDate=${endDate ? endDate : ""}`,
    );
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Fetch astrologer rating history
export const getAstrologerReviews = async ({ id, page = 1, limit = 10 }) => {
  try {
    const response = await api.get(
      `${API_URL}/astrologers/getReviews/${id}?page=${page}&limit=${limit}`,
    );
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// review approval api here
export const updateReviewStatus = async (reviewId, approved) => {
  try {
    const response = await api.patch(
      `${API_URL}/astrologers/review_approval/${reviewId}`,
      { approved },
    );
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// Fetch astrologer call history
export const getAstrologerCallHistory = async ({
  astrologer_id,
  page = 1,
  limit = 10,
  startDate = "",
  endDate = "",
  sortOrder = "desc",
  callStatus = "",
  userSearch = "",
  callType = "",
}) => {
  try {
    const response = await api.get(`${API_URL}/callHistory`, {
      params: {
        astrologer_id,
        page,
        limit,
        startDate,
        endDate,
        sortOrder,
        callStatus,
        userSearch,
        callType,
      },
    });

    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
  }
};

// ✅ NEW — Fetch signed recording URL for a voice call
export const getCallRecording = async (callId) => {
  try {
    const response = await api.get(
      `${API_URL}/callHistory/recording/${callId}`,
    );
    return response.data; // { success, message, data: { call_id, recordings: [{ signed_url }] } }
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// Fetch astrologer chat history (no pagination, only date filters)
export const getAstrologerChatHistory = async ({
  astrologer_id,
  from = "",
  to = "",
}) => {
  try {
    const response = await api.get(
      `${API_URL}/astrologers/getOnlineStats/${astrologer_id}`,
      {
        params: {
          from,
          to,
        },
      },
    );
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// ================= CHAT USERS =================
// export const getChatUsersList = async ({
//   astrologer_id,
//   page = 1,
//   limit = 10,
// }) => {
//   try {
//     const response = await api.get(`${API_URL}/chat/list`, {
//       params: { astrologer_id, page, limit },
//     });
//     return response.data;
//   } catch (error) {
//     handleUnauthorizedOrForbidden(error);
//     throw error;
//   }
// };

export const getChatUsersList = async ({
  astrologer_id,
  page = 1,
  limit = 10,
  search = "",
}) => {
  try {
    const response = await api.get(`${API_URL}/chat/list`, {
      params: {
        astrologer_id,
        page,
        limit,
        search, // 🔥 ADD THIS
      },
    });

    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// ================= CHAT MESSAGES =================
export const getChatMessagesList = async ({
  astrologer_id,
  user_id,
  page = 1,
  limit = 20,
}) => {
  try {
    const response = await api.get(`${API_URL}/chat/messages`, {
      params: { astrologer_id, user_id, page, limit },
    });
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error);
    throw error;
  }
};

// Add a new astrologer
export const addAstrologer = async (astrologer) => {
  try {
    const response = await api.post(`${API_URL}/astrologers`, astrologer, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Update an astrologer
export const updateAstrologer = async (id, astrologer) => {
  try {
    const response = await api.put(`${API_URL}/astrologers/${id}`, astrologer, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Update an astrologer status
export const updateAstrologerStatus = async (id, updateStatusObj) => {
  try {
    const response = await api.put(
      `${API_URL}/astrologers/${id}/status`,
      updateStatusObj,
    );
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Delete an astrologer
export const deleteAstrologer = async (id) => {
  try {
    const response = await api.delete(`${API_URL}/astrologers/${id}`);
    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Settle astrologer payment (Admin)
export const settleAstrologerPayment = async ({
  astrologer_id,
  amount,
  note,
  payment_mode,
}) => {
  try {
    const response = await api.post(`${API_URL}/astrologers/settle_payment`, {
      astrologer_id,
      amount,
      payment_mode,
      note,
    });

    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};

// Fetch astrologer payout / payment history (Admin)
export const getAstrologerPayoutHistory = async ({
  id,
  page = 1,
  limit = 10,
  startDate = "",
  endDate = "",
}) => {
  try {
    const response = await api.get(
      `${API_URL}/astrologers/getPayoutHistory/${id}?page=${page}&limit=${limit}&startDate=${startDate ? startDate : ""
      }&endDate=${endDate ? endDate : ""}`,
    );

    return response.data;
  } catch (error) {
    handleUnauthorizedOrForbidden(error); // Handle 401/403 errors and clear cookies
  }
};
